import React, { useState, useEffect } from 'react';
import { ExternalLink, Calendar, User, DollarSign, AlertCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/Card';
import Badge from '../ui/Badge';
import { supabase } from '../../lib/supabase';

interface RecentOrder {
  id: string;
  order_number: string;
  customer_name?: string;
  supplier_name?: string;
  total_amount: number;
  status: string;
  created_at: string;
  type: 'sales' | 'purchase';
  we_owe_count?: number;
}

const RecentOrders: React.FC = () => {
  const [recentOrders, setRecentOrders] = useState<RecentOrder[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadRecentOrders();
  }, []);

  const loadRecentOrders = async () => {
    try {
      // Load recent sales orders
      const { data: salesOrders } = await supabase
        .from('sales_orders')
        .select('id, order_number, customer_name, total_amount, status, created_at')
        .order('created_at', { ascending: false })
        .limit(3);

      // Load recent purchase orders
      const { data: purchaseOrders } = await supabase
        .from('purchase_orders')
        .select(`
          id,
          order_number,
          total_amount,
          status,
          created_at,
          suppliers (name)
        `)
        .order('created_at', { ascending: false })
        .limit(3);

      // Load We Owe counts for sales orders
      const salesOrderIds = salesOrders?.map(o => o.id) || [];
      const weOweCounts = new Map<string, number>();

      if (salesOrderIds.length > 0) {
        const { data: weOweItems } = await supabase
          .from('we_owe_items')
          .select('sales_order_id')
          .eq('status', 'pending')
          .in('sales_order_id', salesOrderIds);

        weOweItems?.forEach(item => {
          const count = weOweCounts.get(item.sales_order_id) || 0;
          weOweCounts.set(item.sales_order_id, count + 1);
        });
      }

      // Combine and format orders
      const allOrders: RecentOrder[] = [
        ...(salesOrders?.map(order => ({
          ...order,
          type: 'sales' as const,
          customer_name: order.customer_name,
          we_owe_count: weOweCounts.get(order.id) || 0
        })) || []),
        ...(purchaseOrders?.map(order => ({
          ...order,
          type: 'purchase' as const,
          supplier_name: order.suppliers?.name
        })) || [])
      ];

      // Sort by created_at and take the 5 most recent
      const sortedOrders = allOrders
        .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
        .slice(0, 8);

      setRecentOrders(sortedOrders);
    } catch (error) {
      console.error('Error loading recent orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case 'pending':
      case 'draft':
        return 'warning';
      case 'processing':
      case 'ordered':
        return 'primary';
      case 'shipped':
      case 'delivered':
      case 'received':
        return 'success';
      case 'cancelled':
        return 'danger';
      default:
        return 'secondary';
    }
  };

  if (loading) {
    return (
      <Card className="overflow-hidden h-96 transition-all duration-200 hover:shadow-md flex flex-col">
        <CardHeader>
          <CardTitle>Recent Orders</CardTitle>
        </CardHeader>
        <CardContent className="flex-1 flex items-center justify-center">
          <div className="flex justify-center">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-900"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="overflow-hidden h-[28rem] transition-all duration-200 hover:shadow-md flex flex-col">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Recent Orders</CardTitle>
        <div className="flex gap-2">
          <Link 
            to="/sales-orders" 
            className="text-sm text-blue-900 hover:underline"
          >
            Sales
          </Link>
          <span className="text-gray-300">|</span>
          <Link 
            to="/purchase-orders" 
            className="text-sm text-blue-900 hover:underline"
          >
            Purchase
          </Link>
        </div>
      </CardHeader>
      <CardContent className="flex-1 flex flex-col p-0 min-h-0">
        {recentOrders.length === 0 ? (
          <div className="flex-1 flex items-center justify-center">
            <p className="text-sm text-gray-500 text-center">No recent orders.</p>
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto relative scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
            <div className="p-4 space-y-3">
            {recentOrders.map((order) => (
              <div key={`${order.type}-${order.id}`} className="flex items-center justify-between border-b border-gray-100 pb-2 last:border-b-0 last:pb-0">
                <div className="flex items-center">
                  <div className={`p-2 rounded-full ${
                    order.type === 'sales' 
                      ? 'bg-green-50' 
                      : 'bg-indigo-50'
                  }`}>
                    {order.type === 'sales' ? (
                      <User className="h-4 w-4 text-green-600" />
                    ) : (
                      <DollarSign className="h-4 w-4 text-indigo-600" />
                    )}
                  </div>
                  <div className="ml-3">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium text-gray-900 truncate max-w-[120px]">
                        {order.order_number}
                      </p>
                      {order.type === 'sales' && order.we_owe_count && order.we_owe_count > 0 && (
                        <div className="flex items-center gap-1 text-xs text-amber-600" title={`${order.we_owe_count} We Owe items`}>
                          <AlertCircle className="h-3 w-3" />
                          <span>{order.we_owe_count}</span>
                        </div>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 truncate max-w-[120px]">
                      {order.type === 'sales' ? order.customer_name : order.supplier_name}
                    </p>
                    <p className="text-xs text-gray-600 flex items-center truncate">
                      <Calendar className="h-3 w-3 mr-1" />
                      {formatDate(order.created_at)}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="text-right">
                    <p className="text-sm font-medium text-gray-900 whitespace-nowrap">
                      ${order.total_amount.toFixed(2)}
                    </p>
                    <Badge variant={getStatusBadgeVariant(order.status)} className="text-xs">
                      {order.status}
                    </Badge>
                  </div>
                  <Link 
                    to={order.type === 'sales' ? `/sales-orders/${order.id}` : `/purchase-orders/${order.id}`}
                    className="text-blue-900 hover:text-blue-700"
                  >
                    <ExternalLink className="h-4 w-4" />
                  </Link>
                </div>
              </div>
            ))}
            </div>
            {/* Scroll indicator */}
            {recentOrders.length > 5 && (
              <div className="absolute bottom-0 left-0 right-0 h-6 bg-gradient-to-t from-white via-white/80 to-transparent pointer-events-none flex items-end justify-center pb-1">
                <div className="text-xs text-gray-600">Scroll for more</div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default RecentOrders;